import React, { useContext, useEffect, useLayoutEffect, useState } from "react";
import { Button, DocIcon, DownloadIcon, Pagination } from '@pd/pd-ui-kit';
import { Flex } from 'antd';
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import Status from "~/components/Status";
import Wrapper from '~/shared/ui/Wrapper';
import { useURLParamsGetter } from "~/hooks/useURLParamsSetter";
import { IVIDEON_DEALER_REGISTRY } from "~/pages/IvideonDealerRegistry/assets/options";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2 } from "~/shared/ui/TableV2";
import { IvideonDealerFilter, useIvideonDealerFilterValues, useIvideonDealerUrlFilters } from "~/widgets/Filter";

import useGetDataIvideon from "./hooks/useGetDataIvideon";
import ModalReport from "./ModalReport";
import {
  GetCurrentDealerContext,
  GetCurrentDealerContextPrpos,
} from "./getCurrentDealer";
import { getParamsUrl } from "./lib/getParamsUrl";

import s from "./s.less";

const IvideonDealerRegistry = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const { count, page } = useURLParamsGetter<{ count: number; page: number }>();
  const { hasPermission, loading: loadingPermissions } = usePermissions();

  const [modalReportState, setModalReportState] = useState(false);
  const [sort, setSort] = useState<string>("");
  const [ividata, setividata] = useState<any[]>([]);
  const [switched, setSwitched] = useState(false);
  const { currentDealer } = useContext<GetCurrentDealerContextPrpos>(
    GetCurrentDealerContext,
  );
  const { data: dataIvideon, isLoading } = useGetDataIvideon(
    sort,
    count,
    page,
    switched,
  );

  const filterValues = useIvideonDealerFilterValues();
  const { applyFilters } = useIvideonDealerUrlFilters();

  // По умолчанию в фильтр подставляется ДЦ, выбранный на главной странице,
  // при выборе другого ДЦ фильтр переключается на него.
  useLayoutEffect(() => {
    if (currentDealer) {
      applyFilters({ ids: currentDealer.id });
    }
  }, [currentDealer]);

  const arrIncludeEn = ["DealerName", "city", "type", "address"];
  const language = localStorage.getItem("language");
  const isEn = language === "en";

  useEffect(() => {
    const filterKeys = Object.keys(filterValues).filter(
      (key) => filterValues[key as keyof typeof filterValues] != null &&
                filterValues[key as keyof typeof filterValues] !== "" &&
                filterValues[key as keyof typeof filterValues] !== "common.not_selected"
    );

    const transformedKeys = filterKeys.map((key) => {
      if (isEn && arrIncludeEn.includes(key)) {
        return `${key}En`;
      }
      return key;
    });

    const transformedValues = filterKeys.reduce((acc, key) => {
      const rawValue = filterValues[key as keyof typeof filterValues];
      const actualKey = isEn && arrIncludeEn.includes(key) ? `${key}En` : key;

      let value: string | boolean | number = rawValue as string;
      if (value === "true") value = true;
      else if (value === "false") value = false;
      else if (key === "CamerasCount") value = Number(value);

      return { ...acc, [actualKey]: value };
    }, {} as Record<string, string | boolean | number>);

    if (filterKeys.length) {
      setSort(() => getParamsUrl(transformedKeys, transformedValues));
      setSwitched(true);
    } else {
      setSort(() => getParamsUrl([], {}));
      setSwitched(true);
    }
  }, [filterValues]);

  const goBack = () => history.go(-1);
  const goToAnalytics = () => {
    history.push("/ivideon/summary");
  };
  const goToCameraList = ({ id, ivideonAuth }) => {
    const isDealerAuth = ivideonAuth === "Да";
    sessionStorage.setItem("currentDealerId", id);
    return history.push(`/ivideon/dealers/${id}?isDealerAuth=${isDealerAuth}`);
  };

  const { handleCount, handlePage } = usePaginationParams();

  const openModalSelectReport = () => {
    setModalReportState(true);
  };
  const closeModalSelectReport = () => {
    setModalReportState(false);
  };

  useLayoutEffect(() => {
    if (dataIvideon && dataIvideon?.dealers.length) {
      const f = dataIvideon.dealers.map((i: any) => {
        const cameras = i.cameras || {};
        const ownerName = i.ivideonOwnerName || "";
        const serviceCheck = cameras.serviceCheck;
        const saleCheck = cameras.saleCheck;
        return {
          ...i,
          name: i.dealerName || i.name,
          address: i.reestrAddress || i.address,
          owner: ownerName,
          status: i.status === "active" ? "Активный" : "Не активный",
          extendedForNextYear: i.extendedForNextYear ? "Да" : "Нет",
          checkStatus: (
            <Status
              name={i.checkStatus ? "verified" : "notVerified"}
              clickable
              onClick={() => {}}
            />
          ),
          serviceCameras: cameras.serviceCameras ?? "-",
          saleCameras: cameras.saleCameras ?? "-",
          serviceCheck: serviceCheck ? "Проверен" : "Не проверен",
          saleCheck: saleCheck ? "Проверен" : "Не проверен",
          ivideonAuth: i.ivideonAuthorized ?? i.ivideonAuth ? "Да" : "Нет",
        };
      });
      setividata(f);
    } else {
      setividata([]);
    }
  }, [dataIvideon, count, page]);

  const goToRegisterOfDocuments = () => {
    history.push("/ivideon/register-of-documents");
  };

  return (
    <Wrapper.Content
      title={t("ivideonCameras.dealerRegistry")}
      txtBtn={t('ui.back')}
      goBackHandler={goBack}
      filterComponent={<IvideonDealerFilter />}
      controlComponent={
        <Flex gap={10}>
          {hasPermission("ivideon.getDealerDocs") && (
            <Button
              onClick={goToRegisterOfDocuments}
              size={'middle'}
              color={'secondary'}
              icon={<DocIcon size={16}/>}
            >
              База документов
            </Button>
          )}
          {hasPermission("ivideon.getReport") && (
            <Button
              onClick={goToAnalytics}
              size={'middle'}
              color={'secondary'}
            >
              Аналитика
            </Button>
          )}
        </Flex>
      }
    >
      <div className={s.container}>
        {hasPermission("ivideon.getReport") && (
          <Button
            onClick={openModalSelectReport}
            size={'medium'}
            color={'secondary'}
            variant={'filled'}
            icon={<DownloadIcon size={12} fill="#495a64" />}
            radius={30}
          >
            Отчет
          </Button>
        )}
        <div className={s.titleContainer}>
          <div className={s.actions}>
          </div>
        </div>
        {!ividata.length && !isLoading ? (
          <NotData />
        ) : (
          <div className={s.wrapp}>
            <TableV2
              wrapperClassName={s.tableWrapper}
              options={IVIDEON_DEALER_REGISTRY}
              dataSource={ividata}
              loading={isLoading}
              type="ivideonCameras.tableDealers"
              rowKey="id"
              scroll={{ x: "max-content" }}
              onRow={(record) => ({
                onClick: () => goToCameraList(record),
              })}
            />
          </div>
        )}
        <Pagination
          count={Number(count) || 25}
          page={(Number(page) || 0) + 1}
          totalCount={dataIvideon?.totalCount || 0}
          onChangeCount={handleCount}
          onChangePage={handlePage}
        />
        <ModalReport
          sort={sort}
          state={modalReportState}
          closeModalSelectReport={closeModalSelectReport}
        />
      </div>
    </Wrapper.Content>
  );
};

const NotData = () => {
  return <div className={s.notData}>Нет данных</div>;
};

export default IvideonDealerRegistry;
